import React from 'react'
import Helmet from 'react-helmet'
import { SchoolHelper } from '@laputan/helpers'
import { School } from '../../containers/props/School.types'
import { CONFIG } from '../../config'

interface EducationOrganizationJsonLdProps {
  school: School
}

// refer to https://schema.org/EducationalOrganization
export const createSubOrganisation = (
  schoolType:
    | 'CollegeOrUniversity'
    | 'ElementarySchool'
    | 'HighSchool'
    | 'MiddleSchool'
    | 'Preschool'
    | 'School',
  school: School
) => {
  if (
    (schoolType === 'Preschool' && school.level?.elc) ||
    (schoolType === 'ElementarySchool' && school.level?.primary) ||
    (schoolType === 'MiddleSchool' && school.level?.secondary) ||
    (schoolType === 'HighSchool' && school.level?.secondary)
  ) {
    const contact = school?.contact || {}
    const location = school?.location || {}
    return {
      '@type': schoolType,
      name: school.name,
      ...(contact.phone ? { telephone: contact.phone } : {}),
      ...(contact.website ? { '@id': contact.website } : {}),
      ...(contact.website ? { url: contact.website } : {}),
      ...(school.logo ? { image: school.logo } : {}),
      address: {
        '@type': 'PostalAddress',
        ...(location.address ? { streetAddress: location.address } : {}),
        ...(location.suburb ? { addressLocality: location.suburb } : {}),
        ...(location.state ? { addressRegion: location.state } : {}),
        ...(location.postcode ? { postalCode: location.postcode } : {}),
        addressCountry: 'AU',
      },
    }
  }
}

export const parseEducationOrganizationJsonLd = (school: School) => {
  const location = school.location || {}
  const contact = school.contact || {}
  const logo = school.logo!
  const imageSize = 200
  const url = SchoolHelper.getSchoolImagePath({
    logo,
    size: imageSize,
  })
  const jsonLd: object = {
    '@context': 'http://schema.org',
    '@type': 'EducationalOrganization',
    name: 'School',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'AU',
      ...(location.suburb ? { addressLocality: location.suburb } : {}),
      ...(location.state ? { addressRegion: location.state } : {}),
      ...(location.postcode ? { postalCode: String(location.postcode) } : {}),
      ...(location.address ? { streetAddress: location.address } : {}),
      ...(contact.phone ? { telephone: contact.phone } : {}),
      ...(school.description ? { description: school.description } : {}),
    },
    areaServed: location.suburb,
    ...(contact.website ? { '@id': contact.website } : {}),
    url: `${CONFIG.APP_URL}${SchoolHelper.normalizeSchoolUrlHandle(
      school,
      true
    )}`,
    ...(url ? { logo: url } : {}),
    ...(url ? { image: url } : {}),
    ...(contact.email ? { email: contact.email } : {}),
    sameAs: [
      contact.facebook,
      contact.twitter,
      contact.instagram,
      contact.linkedIn,
      contact.youtube,
    ].filter((a) => a),
    subOrganization: [
      createSubOrganisation('Preschool', school),
      createSubOrganisation('ElementarySchool', school),
      createSubOrganisation('MiddleSchool', school),
      createSubOrganisation('HighSchool', school),
      createSubOrganisation('CollegeOrUniversity', school),
    ].filter((a) => a),
  }
  return jsonLd
}

const EducationOrganizationJsonLd = ({
  school,
}: EducationOrganizationJsonLdProps) => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(parseEducationOrganizationJsonLd(school))}
      </script>
    </Helmet>
  )
}

export default EducationOrganizationJsonLd
